import { BaseWidgetProps, SkeletonWrapper, WidgetWrapper } from "shared-ui";
import type { FC } from "react";
import Grid from "@mui/material/Grid";
import { useGetYourShiTeam } from "@features/business-review-feature/hooks/useGetYourShiTeam";
import ShiTeamMember from "./ShiTeamMember";
import { Box, Link, Typography } from "@mui/material";
import InfoIcon from "@mui/icons-material/Info";

const EmptyTeamContent = () => (
  <Box
    sx={{
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      padding: 2,
      textAlign: "center",
    }}
  >
    <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
      <InfoIcon color="disabled" fontSize="medium" />
      <Typography color="text.secondary" variant="body1" fontWeight="normal">
        No Account Information Yet
      </Typography>
    </Box>
    <Typography color="text.secondary" variant="body2">
      Don't see your Account Team? Contact your AE or email
    </Typography>
    <Typography color="text.secondary" variant="body2">
      <Link href="mailto:supportservices@shi.com" variant="body2">
        supportservices@shi.com
      </Link>{" "}
      for resolution.
    </Typography>
  </Box>
);

const ShiTeamWidget: FC<BaseWidgetProps> = ({ pageLayoutWidget }) => {
  const { data, isLoading, isError } = useGetYourShiTeam();

  const hasTeamData =
    !!data?.primaryAccountExecutive ||
    !!data?.yourShiTeamMembers?.length;

  if (isError || (!isLoading && !hasTeamData)) {
    return (
      <WidgetWrapper pageLayoutWidget={pageLayoutWidget}>
        <EmptyTeamContent />
      </WidgetWrapper>
    );
  }

  return (
    <WidgetWrapper pageLayoutWidget={pageLayoutWidget}>
      <Grid container spacing={1}>
        {data?.primaryAccountExecutive && (
          <Grid size={6} sx={{ minHeight: "64px" }}>
            <SkeletonWrapper loading={isLoading} error={isError}>
              <ShiTeamMember
                shiEmployeeId={data.primaryAccountExecutive.shiEmployeeId}
                fullName={data.primaryAccountExecutive.fullName}
                customerFacingTitle={
                  data.primaryAccountExecutive.customerFacingTitle
                }
                customerFacingEmailAddress={
                  data.primaryAccountExecutive.customerFacingEmailAddress
                }
              />
            </SkeletonWrapper>
          </Grid>
        )}
        {/* empty grid item beside account executive is intentional */}
        {data?.primaryAccountExecutive && <Grid size={6} />}
        {data?.yourShiTeamMembers?.map((member) => (
          <Grid size={6} key={member.shiEmployeeId}>
            <SkeletonWrapper loading={isLoading}>
              <ShiTeamMember
                shiEmployeeId={member.shiEmployeeId}
                fullName={member.fullName}
                customerFacingTitle={member.customerFacingTitle}
                customerFacingEmailAddress={member.customerFacingEmailAddress}
              />
            </SkeletonWrapper>
          </Grid>
        ))}
      </Grid>
    </WidgetWrapper>
  );
};

export default ShiTeamWidget;
